import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@/translations';
import {
  useDeleteDocumentType,
  useDocumentTypes,
} from '@/api/DocumentManagement';

import { App, Button, Modal } from 'antd';
import { CustomColumns, CustomTable, Icon } from '@/ui/components';
import { DocumentType } from '@/types';
import { StyledLink } from '@/ui/globalStyles';
import { faCheck, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Title } from './DocumentTypesModal.styled';
import { DocumentTypesModalProps } from './types';
import { AddDocumentTypeModal } from '../AddDocumentTypeModal';

export const DocumentTypesModal: FC<DocumentTypesModalProps> = memo(
  ({ isOpen, closeModal }) => {
    const { data: documentTypes, isFetching } = useDocumentTypes();
    const { mutate: deleteDocumentTypeMutate } = useDeleteDocumentType();
    const [openAddDocumentTypeModal, setAddDocumentTypeModal] = useState(false);
    const { t } = useTranslate();
    const { modal } = App.useApp();

    const onDeleteDocumentType = useCallback(
      (documentTypeId: number) => {
        modal.confirm({
          title: 'Confirm',
          content: t('deleteRecordQuestion'),
          okText: t('yes'),
          onOk: () => deleteDocumentTypeMutate(documentTypeId),
          onCancel: () => {},
        });
      },
      [deleteDocumentTypeMutate, modal, t],
    );

    const columns: CustomColumns<DocumentType> = useMemo(
      () => [
        { dataIndex: 'customerName', title: t('customer') },
        { dataIndex: 'documentTypeCode', title: 'Document Type Code' },
        { dataIndex: 'documentType', title: 'Document Type' },
        {
          dataIndex: 'isCustomerPreviewRestricted',
          title: 'Customer Preview Restricted',
          render: (value) => (value ? <Icon icon={faCheck} /> : null),
        },
        {
          dataIndex: 'documentTypeId',
          title: 'Actions',
          align: 'center',
          render: (value) => (
            <StyledLink onClick={() => onDeleteDocumentType(value)}>
              <Icon icon={faTrash} />
            </StyledLink>
          ),
        },
      ],
      [onDeleteDocumentType, t],
    );

    return (
      <>
        <Modal
          open={isOpen}
          onCancel={closeModal}
          width="50vw"
          loading={isFetching}
          title={
            <Button
              type="primary"
              onClick={() => setAddDocumentTypeModal(true)}
            >
              {t('addNew')}
            </Button>
          }
          centered
          footer={null}
        >
          <Title>{t('documentTypes')}</Title>
          <CustomTable
            dataSource={documentTypes || []}
            columns={columns}
            rowKey="documentTypeId"
          />
        </Modal>

        <AddDocumentTypeModal
          isOpen={openAddDocumentTypeModal}
          closeModal={() => setAddDocumentTypeModal(false)}
        />
      </>
    );
  },
);
